import { useCallback, useEffect, useRef, useState } from 'react'

import { FileItem, MIME_TYPES } from '@inno-sdks/files'
import { toast } from '@inno-ui/toast'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

import { useGetFileItem } from '../api'
import { useConvertToPdfMutation } from '../api/requests/convertToPdf'
import { useDownloadFileMutation } from '../api/requests/downloadFile'
import { usePrintStore } from '../store/printStore'
import { isPdfProtectedOrCorrupted, printBlob } from '../utils'

export const usePrintFile = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop'
  })

  const [itemId, setItemId] = useState<number>()
  const isPrintTriggered = useRef(false)

  const { mutateAsync: downloadFile } = useDownloadFileMutation()
  const { mutateAsync: convertToPdf } = useConvertToPdfMutation()

  const { data } = useGetFileItem<FileItem>(itemId)

  const shouldSkipPdfConversion = useCallback((mimeType: string) => {
    const isPdf = mimeType === MIME_TYPES.pdf.pdf
    const isImage = Object.values(MIME_TYPES.image).includes(mimeType)
    return isPdf || isImage
  }, [])

  const isPdfValidationRequired = useCallback(
    (mimeType: string) => !Object.values(MIME_TYPES.image).includes(mimeType),
    []
  )

  const handlePrint = useCallback(async (item: FileItem) => {
    const { addItem, markCompleted, markFailed, cancelPrint, updateProgress } =
      usePrintStore.getState()

    if (item.metaData.size === 0) {
      toast.dismiss()
      return toast.warning({
        title: t('empty-file-warning.title'),
        subTitle: t('empty-file-warning.subtitle')
      })
    }

    const abortController = new AbortController()
    const skipPdfConversion = shouldSkipPdfConversion(item.metaData.mimeType)

    try {
      addItem({
        id: item.id,
        label: item.name,
        progress: 0,
        abortController,
        file: item,
        status: 'pending',
        onCancel: () => cancelPrint(item.id)
      })

      const blob = await downloadFile({
        signedUrl: item.metaData.presignedUrl!,
        mimeType: item.metaData.mimeType,
        onProgress: progress => {
          const normalizedProgress = Math.floor(
            progress * (skipPdfConversion ? 1 : 0.5)
          )
          updateProgress(item.id, normalizedProgress)
        },
        abortController
      })

      let pdfBlob: Blob

      if (skipPdfConversion) {
        pdfBlob = blob
      } else {
        const file = new File([blob], item.name, {
          type: item.metaData.mimeType,
          lastModified: Date.now()
        })

        pdfBlob = await convertToPdf({
          file,
          abortController,
          onProgress: progress => {
            const normalizedProgress = Math.floor(50 + progress * 50)
            updateProgress(item.id, normalizedProgress)
          }
        })
      }

      if (isPdfValidationRequired(item.metaData.mimeType)) {
        const _isPdfProtectedOrCorrupted =
          await isPdfProtectedOrCorrupted(pdfBlob)

        if (_isPdfProtectedOrCorrupted) {
          return markFailed(
            item.id,
            _isPdfProtectedOrCorrupted === 'corrupted'
              ? t('corrupted-pdf-error')
              : t('password-protected-pdf-error')
          )
        }
      }

      printBlob(pdfBlob, () => markCompleted(item.id, pdfBlob))
    } catch (error) {
      if (!axios.isCancel(error)) {
        markFailed(item.id, t('print-error'))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isPrintTriggered.current || !data?.metaData?.presignedUrl) {
      return
    }

    handlePrint(data)
    isPrintTriggered.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return useCallback((itemId: number) => {
    setItemId(itemId)
  }, [])
}
